import { useQuery } from '@tanstack/react-query';
import { Pagination } from 'antd';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CenterFlex, Divider, Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import TabRadio from '@/components/TabRadio/TabRadio';
import Typo, { Span } from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { scrapingService } from '@/features/Scraping/api';
import FloatActionMenu from '@/features/Scraping/components/FloatActionMenu/FloatActionMenu';
import { TaskTable } from '@/features/Scraping/components/TaskTable';
import CreateScrapingJobModal from '@/features/Scraping/modals/CreateScrapingJobModal';
import { ScrapingCSVUploadModal } from '@/features/Scraping/modals/ScrapingCSVUploadModal';
import {
  isCancelableScrapingJob,
  isDeletableScrapingJob,
} from '@/features/Scraping/models/ScrapingJob';
import {
  getJobStatusByDisplayStatus,
  getScrapingJobTypeIcon,
  groupScrapingStatsByDisplay,
  SCRAPING_JOB_DISPLAY_STATUS,
  SCRAPING_JOB_STATUS,
  SCRAPING_JOB_TYPE,
  SCRAPING_SCHEDULE_DISPLAY_STATUS,
} from '@/features/Scraping/types/constants';
import useModalState from '@/hooks/pure/useModalState';
import usePaginatedQuery from '@/hooks/pure/usePaginatedQuery/usePaginatedQuery';
import { cn } from '@/lib/cn';
import service from '@/services';
import { toast } from '@/stores/pure/ToastStore';
import type { SOURCE_TYPE } from '@/types/feedbackConst';
import { track } from '@/utils/track/track';

import { JobStatus } from './components/JobStatus/JobStatus';

export const IntegrationScrapingTab = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedScrapingTab, setSelectedScrapingTab] =
    useState<SCRAPING_JOB_TYPE>(SCRAPING_JOB_TYPE.ONETIME);
  const [checkedList, setCheckedList] = useState<string[]>([]);

  const {
    modalState: createModalState,
    status: createStatus,
    open: createOpen,
    close: createClose,
  } = useModalState<{ scraper: SOURCE_TYPE | undefined }>({
    scraper: undefined,
  });

  const {
    status: bulkStatus,
    open: bulkOpen,
    close: bulkClose,
  } = useModalState();

  const [statusFilter, setStatusFilter] = useState<
    SCRAPING_JOB_DISPLAY_STATUS | SCRAPING_SCHEDULE_DISPLAY_STATUS
  >(SCRAPING_JOB_DISPLAY_STATUS.ALL);

  const { data, pagination, isLoading, refetch } = usePaginatedQuery({
    queryKey: ['scraping-list', selectedScrapingTab, statusFilter],
    fetcher: async (params) => {
      const response = await scrapingService.retrieveScrapingJobs({
        limit: params.pageSize,
        offset: (params.page - 1) * params.pageSize,
        status: (function () {
          if (statusFilter === SCRAPING_JOB_DISPLAY_STATUS.ALL) {
            return [];
          }
          return getJobStatusByDisplayStatus(statusFilter, selectedScrapingTab);
        })(),
        type: selectedScrapingTab,
      });
      return {
        data: response.jobs,
        totalCount: response.total_count,
      };
    },
    onSuccess: () => {
      setCheckedList([]);
    },
  });

  const { data: jobStats, refetch: refetchJobStats } = useQuery({
    queryKey: ['scraping-stats'],
    queryFn: () =>
      service.scraping.retrieveScrapingJobAggregate(
        SCRAPING_JOB_TYPE.ONETIME,
        [],
      ),
    refetchOnWindowFocus: true,
  });

  const { data: scheduleStats, refetch: refetchScheduleStats } = useQuery({
    queryKey: ['scraping-schedule-stats'],
    queryFn: () =>
      service.scraping.retrieveScrapingJobAggregate(
        SCRAPING_JOB_TYPE.SCHEDULED,
        [
          SCRAPING_JOB_STATUS.BEFORE_SCRAPING,
          SCRAPING_JOB_STATUS.BEFORE_CONFIRM,
          SCRAPING_JOB_STATUS.REJECTED,
        ],
      ),
    refetchOnWindowFocus: true,
  });

  const refetchStats = () => {
    refetchJobStats();
    refetchScheduleStats();
  };

  const onCreateRequestClick = (scraper?: SOURCE_TYPE) => {
    track(AMP_KEYS.INTEGRATION_ATTEMPT_START, {
      integration_type: 'scraping',
    });
    createOpen({
      modalState: {
        scraper: scraper,
      },
    });
  };

  const groupedJobStats = groupScrapingStatsByDisplay(
    SCRAPING_JOB_TYPE.ONETIME,
    jobStats,
  );
  const groupedScheduleStats = groupScrapingStatsByDisplay(
    SCRAPING_JOB_TYPE.SCHEDULED,
    scheduleStats,
  );

  const scrapingTabs = useMemo(() => {
    const JobIcon = getScrapingJobTypeIcon(SCRAPING_JOB_TYPE.ONETIME);
    const ScheduleIcon = getScrapingJobTypeIcon(SCRAPING_JOB_TYPE.SCHEDULED);
    return [
      {
        node: (
          <div className="flex items-center gap-1">
            <JobIcon size={12} />
            <Typo type="label3">Jobs</Typo>
            {groupedJobStats[SCRAPING_JOB_DISPLAY_STATUS.NEED_TO_APPROVAL] >
              0 && <div className="w-1 h-1 mb-2 bg-red-700 rounded-full" />}
          </div>
        ),
        key: SCRAPING_JOB_TYPE.ONETIME,
        text: 'Jobs',
      },
      {
        node: (
          <div className="flex items-center gap-1">
            <ScheduleIcon size={12} />
            <Typo type="label3">Schedules</Typo>
            {groupedScheduleStats[
              SCRAPING_JOB_DISPLAY_STATUS.NEED_TO_APPROVAL
            ] > 0 && <div className="w-1 h-1 mb-2 bg-red-700 rounded-full" />}
          </div>
        ),
        key: SCRAPING_JOB_TYPE.SCHEDULED,
        text: 'Schedules',
      },
    ];
  }, [groupedJobStats, groupedScheduleStats]);

  const onUploadSuccess = (type: SCRAPING_JOB_TYPE) => {
    refetch();
    refetchStats();

    if (type === SCRAPING_JOB_TYPE.SCHEDULED) {
      toast('A Schedule has been created', 'success');
    } else {
      toast('A Job has been created', 'success');
    }
  };

  useEffect(() => {
    const scraper = location.state?.scraper;
    if (scraper) {
      onCreateRequestClick(scraper);
      navigate(location.pathname, { replace: true });
    }
  }, []);
  useEffect(() => {
    setStatusFilter(
      selectedScrapingTab === SCRAPING_JOB_TYPE.ONETIME
        ? SCRAPING_JOB_DISPLAY_STATUS.ALL
        : SCRAPING_SCHEDULE_DISPLAY_STATUS.ALL,
    );
  }, [selectedScrapingTab]);

  return (
    <div className="flex flex-1 flex-col overflow-auto px-8 py-6 h-full">
      <div className="flex justify-between items-center">
        <TabRadio
          options={scrapingTabs}
          value={selectedScrapingTab}
          onChange={setSelectedScrapingTab}
          sizeType="m"
          style={{ width: 'fit-content' }}
        />
        <div className="flex gap-3">
          <Button
            sizeType="m"
            variant="outlined"
            leftIcon={<Plus />}
            onClick={() => {
              track(AMP_KEYS.INTEGRATION_ATTEMPT_START, {
                integration_type: 'scraping_bulk',
              });
              bulkOpen();
            }}
          >
            Bulk{' '}
            {selectedScrapingTab === SCRAPING_JOB_TYPE.ONETIME
              ? 'job'
              : 'schedule'}{' '}
            (CSV)
          </Button>
          <Button
            sizeType="m"
            leftIcon={<Plus />}
            onClick={() => {
              onCreateRequestClick();
            }}
          >
            {selectedScrapingTab === SCRAPING_JOB_TYPE.ONETIME
              ? 'Job'
              : 'Schedule'}
          </Button>
        </div>
      </div>
      <Space h={32} />
      <Flex g={8} w="wrap">
        {(function () {
          return Object.entries(
            selectedScrapingTab === SCRAPING_JOB_TYPE.ONETIME
              ? groupedJobStats
              : groupedScheduleStats,
          ).map(([displayStatus, count]) => {
            const displayStatusEnum =
              displayStatus as SCRAPING_JOB_DISPLAY_STATUS;
            return (
              <div
                className={cn(
                  'flex gap-1 items-center border border-gray-300 rounded-md h-6 px-2 cursor-pointer text-gray-700 hover:border-primary transition-all duration-200',
                  {
                    'border-primary text-gray-900':
                      displayStatusEnum === statusFilter,
                  },
                )}
                key={displayStatus}
                onClick={() => setStatusFilter(displayStatusEnum)}
              >
                <JobStatus status={displayStatusEnum} />
                <Span size={3} weight="r">
                  ({count || 0})
                </Span>
                {displayStatus ===
                  SCRAPING_JOB_DISPLAY_STATUS.NEED_TO_APPROVAL &&
                count &&
                count > 0 ? (
                  <div className="w-1 h-1 mb-2 bg-red-700 rounded-full" />
                ) : undefined}
              </div>
            );
          });
        })()}
      </Flex>
      <Space h={24} />
      <TaskTable
        data={data}
        isLoading={isLoading}
        refetch={refetch}
        refetchStats={refetchStats}
        selectedTab={selectedScrapingTab}
        onCreateClick={onCreateRequestClick}
        checkedList={checkedList}
        onCheck={setCheckedList}
      />
      <Divider />
      <Space h={24} />
      <CenterFlex>
        <Pagination
          current={pagination.currentPage}
          total={pagination.totalCount}
          pageSize={pagination.pageSize}
          onChange={(page) => pagination.goToPage(page)}
          showSizeChanger={false}
        />
      </CenterFlex>
      <FloatActionMenu
        isAllSelected={
          checkedList.length ===
            data.filter((item) => {
              return (
                isCancelableScrapingJob(item) || isDeletableScrapingJob(item)
              );
            }).length && data.length > 0
        }
        onCheckAll={() =>
          setCheckedList(
            data
              .filter((item) => {
                return (
                  isCancelableScrapingJob(item) || isDeletableScrapingJob(item)
                );
              })
              .map((item) => item.alias),
          )
        }
        selectedList={checkedList}
        scrapingJobType={selectedScrapingTab}
        onClear={() => setCheckedList([])}
        refetchCallback={refetch}
      />
      <Modal
        status={createStatus}
        closeModal={createClose}
        useBackgroundClose
        useEscapeClose
      >
        <CreateScrapingJobModal
          initialSourceType={createModalState.scraper}
          jobType={selectedScrapingTab}
          onSuccess={onUploadSuccess}
        />
      </Modal>

      <Modal
        status={bulkStatus}
        closeModal={bulkClose}
        useBackgroundClose
        useEscapeClose
      >
        <ScrapingCSVUploadModal
          onSuccess={onUploadSuccess}
          jobType={selectedScrapingTab}
        />
      </Modal>
    </div>
  );
};
